import { useCallback, useEffect, useRef, useState } from "react";
import type { Socket } from "socket.io-client";
import type React from "react";

// Maps browser KeyboardEvent.key to the names input.exe (and the legacy
// robotjs path on the backend) expects. Single-character keys are passed
// through as-is when they're not in this map.
const ROBOTJS_KEY_MAP: Record<string, string> = {
  " ": "space", Enter: "enter", Backspace: "backspace", Delete: "delete",
  Tab: "tab", Escape: "escape", ArrowLeft: "left", ArrowRight: "right",
  ArrowUp: "up", ArrowDown: "down", Home: "home", End: "end",
  PageUp: "pageup", PageDown: "pagedown", Control: "control",
  Shift: "shift", Alt: "alt", Meta: "command", CapsLock: "capslock",
  F1: "f1", F2: "f2", F3: "f3", F4: "f4", F5: "f5", F6: "f6",
  F7: "f7", F8: "f8", F9: "f9", F10: "f10", F11: "f11", F12: "f12",
};

const getButton = (e: React.MouseEvent) =>
  e.button === 2 ? "right" : e.button === 1 ? "middle" : "left";

interface UseStreamInputArgs {
  socket: Socket | null;
  systemId: number;
  videoRef: React.RefObject<HTMLVideoElement>;
  videoWrapperRef: React.RefObject<HTMLDivElement>;
  // When false, all handlers no-op and the wheel listener isn't attached.
  // Used by MultiViewModal to flip control on/off for a pane without
  // tearing it down.
  enabled: boolean;
}

interface UseStreamInputReturn {
  handleMouseDown: React.MouseEventHandler<HTMLDivElement>;
  handleMouseUp: React.MouseEventHandler<HTMLDivElement>;
  handleMouseMove: React.MouseEventHandler<HTMLVideoElement>;
  handleKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
  // Consumer calls this from pc.ondatachannel when an "input" channel
  // arrives. Pass null when the channel closes / peer connection tears
  // down. The hook owns the channel ref internally.
  bindInputChannel: (channel: RTCDataChannel | null) => void;
  // True while the input data channel is open. Surface for UI hints.
  inputOverDC: boolean;
}

// Self-contained input pipeline for a single live-view stream. Sends mouse
// and keyboard events to the system app, preferring a WebRTC data channel
// (lower latency, fully P2P over TURN-direct) and falling back to socket.io
// when the channel isn't open. Lifted out of LiveStreamModal so the
// MultiView focused pane can reuse it without duplicating subtle bits like
// the click-precision MOUSE_MOVE-before-MOUSE_DOWN sync or the rAF-batched
// wheel pipeline.
export function useStreamInput({
  socket,
  systemId,
  videoRef,
  videoWrapperRef,
  enabled,
}: UseStreamInputArgs): UseStreamInputReturn {
  const inputChannelRef = useRef<RTCDataChannel | null>(null);
  const [inputOverDC, setInputOverDC] = useState(false);
  // Last normalized cursor position over the video CONTENT (not the
  // element box). Used to land the click exactly where the operator
  // pointed even if the throttled MOUSE_MOVE hasn't caught up yet.
  const cursorPosRef = useRef<{ x: number; y: number } | null>(null);
  const lastMouseEmit = useRef(0);

  // Keep `enabled` in a ref so the stable handlers below don't get
  // recreated every time a parent re-renders for an unrelated reason.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const bindInputChannel = useCallback((channel: RTCDataChannel | null) => {
    if (!channel) {
      inputChannelRef.current = null;
      setInputOverDC(false);
      return;
    }
    inputChannelRef.current = channel;
    channel.onopen = () => setInputOverDC(true);
    channel.onclose = () => {
      if (inputChannelRef.current === channel) inputChannelRef.current = null;
      setInputOverDC(false);
    };
  }, []);

  // Prefer the WebRTC data channel; fall back to socket.io. Keeping the
  // socket.io path lets the operator interact during the brief window
  // between ICE connected and the input channel actually opening.
  const sendInput = useCallback((payload: any) => {
    if (!enabledRef.current) return;
    const dc = inputChannelRef.current;
    if (dc && dc.readyState === "open") {
      try {
        dc.send(JSON.stringify(payload));
        return;
      } catch (_) {
        // fall through to socket
      }
    }
    if (!socket) return;
    if (payload.type === "MOUSE_MOVE") socket.emit("MOUSE_MOVE", { x: payload.x, y: payload.y, systemId });
    else if (payload.type === "MOUSE_DOWN") socket.emit("MOUSE_DOWN", { button: payload.button, systemId });
    else if (payload.type === "MOUSE_UP") socket.emit("MOUSE_UP", { button: payload.button, systemId });
    else if (payload.type === "MOUSE_WHEEL") socket.emit("MOUSE_WHEEL", { x: payload.x, y: payload.y, systemId });
    else if (payload.type === "KEY_EVENT") socket.emit("KEY_EVENT", { key: payload.key, modifiers: payload.modifiers, systemId });
  }, [socket, systemId]);

  const handleMouseDown = useCallback<React.MouseEventHandler<HTMLDivElement>>((e) => {
    if (!enabledRef.current) return;
    e.preventDefault();
    // Click precision: emit the exact click position as MOUSE_MOVE right
    // before MOUSE_DOWN so the remote OS cursor is at the right place
    // when the click lands. Without this, the system's cursor would be
    // at wherever the last throttled MOUSE_MOVE left it (up to 200ms
    // stale) and a click on a small UI target could miss.
    if (cursorPosRef.current) {
      sendInput({
        type: "MOUSE_MOVE",
        x: cursorPosRef.current.x,
        y: cursorPosRef.current.y,
      });
      lastMouseEmit.current = Date.now();
    }
    sendInput({ type: "MOUSE_DOWN", button: getButton(e) });
  }, [sendInput]);

  const handleMouseUp = useCallback<React.MouseEventHandler<HTMLDivElement>>((e) => {
    if (!enabledRef.current) return;
    e.preventDefault();
    sendInput({ type: "MOUSE_UP", button: getButton(e) });
  }, [sendInput]);

  const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLDivElement>>((e) => {
    if (!enabledRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const isSingleChar = e.key.length === 1;
    const usingShortcut = e.ctrlKey || e.altKey;
    // Plain typing: forward e.key as-is so the browser's applied case
    // (factoring in local Shift + Caps Lock) reaches the remote. For
    // Ctrl+letter / Alt+letter shortcuts we lowercase so local Caps Lock
    // doesn't accidentally turn Ctrl+A into Ctrl+Shift+A.
    const key =
      ROBOTJS_KEY_MAP[e.key] ??
      (isSingleChar ? (usingShortcut ? e.key.toLowerCase() : e.key) : null);
    if (!key) return;
    const modifiers: string[] = [];
    if (e.ctrlKey && e.key !== "Control") modifiers.push("control");
    // Only emit Shift as a modifier when it's part of a shortcut or a
    // non-character key. For plain typing the case is already in the
    // character we're sending — emitting Shift separately would double-
    // shift and break things like Caps Lock state.
    if (e.shiftKey && e.key !== "Shift" && (!isSingleChar || usingShortcut)) {
      modifiers.push("shift");
    }
    if (e.altKey && e.key !== "Alt") modifiers.push("alt");
    sendInput({ type: "KEY_EVENT", key, modifiers });
  }, [sendInput]);

  const handleMouseMove = useCallback<React.MouseEventHandler<HTMLVideoElement>>((e) => {
    if (!enabledRef.current) return;
    const video = e.currentTarget;
    const rect = video.getBoundingClientRect();

    const pxX = e.clientX - rect.left;
    const pxY = e.clientY - rect.top;

    // With objectFit: contain, the actual video content is centered
    // inside the element box with black bars filling unused space.
    // Normalize against the content rect, not the element box, so clicks
    // land on the right remote pixel regardless of letterboxing.
    const eleW = rect.width;
    const eleH = rect.height;
    const vidW = video.videoWidth || eleW;
    const vidH = video.videoHeight || eleH;
    let contentX = 0, contentY = 0, contentW = eleW, contentH = eleH;
    if (vidW > 0 && vidH > 0) {
      const eleRatio = eleW / eleH;
      const vidRatio = vidW / vidH;
      if (vidRatio > eleRatio) {
        contentH = eleW / vidRatio;
        contentY = (eleH - contentH) / 2;
      } else if (vidRatio < eleRatio) {
        contentW = eleH * vidRatio;
        contentX = (eleW - contentW) / 2;
      }
    }

    const normX = Math.max(0, Math.min(1, (pxX - contentX) / contentW));
    const normY = Math.max(0, Math.min(1, (pxY - contentY) / contentH));

    cursorPosRef.current = { x: normX, y: normY };

    // Wire-level MOUSE_MOVE throttled to 5/s so remote OS keeps getting
    // periodic position updates (hover tooltips, focus rectangles) but
    // we don't saturate the channel. Click precision is guaranteed by
    // handleMouseDown emitting an explicit MOUSE_MOVE at click position.
    const MOUSE_MOVE_WIRE_MS = 200;
    const now = Date.now();
    if (now - lastMouseEmit.current < MOUSE_MOVE_WIRE_MS) return;
    lastMouseEmit.current = now;
    sendInput({
      type: "MOUSE_MOVE",
      x: parseFloat(normX.toFixed(4)),
      y: parseFloat(normY.toFixed(4)),
    });
  }, [sendInput]);

  // Wheel listener is attached imperatively because React's synthetic
  // onWheel uses a PASSIVE listener, which silently ignores
  // e.preventDefault(). addEventListener with { passive: false } is the
  // only way to actually block the page behind from scrolling.
  //
  // Deltas are rAF-batched: trackpad momentum scrolling fires 100+
  // events/sec, each one calling JSON.stringify + dc.send(). Without
  // batching, sustained scrolling saturates the renderer send loop and
  // the page hangs briefly (especially noticeable when closing mid-
  // scroll: cleanup runs while sends are still queued, freezing the
  // video). One coalesced send per frame is visually smooth and caps
  // the rate at ~60/sec regardless of input device.
  useEffect(() => {
    if (!enabled) return;
    const el = videoWrapperRef.current;
    if (!el) return;
    const accum = { x: 0, y: 0 };
    let rafId: number | null = null;

    const flush = () => {
      rafId = null;
      let dx = accum.x;
      let dy = accum.y;
      accum.x = 0;
      accum.y = 0;
      // Cross-axis deadzone — trackpads send tiny lateral noise during
      // vertical scrolling. Real diagonal scroll has comparable
      // magnitudes on both axes and passes through untouched.
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      if (absX < 5 && absX < absY * 0.3) dx = 0;
      if (absY < 5 && absY < absX * 0.3) dy = 0;
      if (dx === 0 && dy === 0) return;
      sendInput({ type: "MOUSE_WHEEL", x: Math.round(dx), y: Math.round(dy) });
    };

    const onWheelNative = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const factor = e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? 400 : 1;
      accum.x += e.deltaX * factor;
      accum.y += e.deltaY * factor;
      if (rafId === null) rafId = requestAnimationFrame(flush);
    };

    el.addEventListener("wheel", onWheelNative, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheelNative);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [enabled, videoWrapperRef, sendInput]);

  // Avoid unused-var TS errors — videoRef isn't read directly by the
  // hook yet, but consumers pass it in so the API is parallel to the
  // other refs and future tweaks (e.g. focusing the video element on
  // control acquire) won't change the signature.
  void videoRef;

  return {
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
    handleKeyDown,
    bindInputChannel,
    inputOverDC,
  };
}
